import * as Sentry from '@sentry/react';
import legacyCookieCleanup from 'utils/legacyCookieCleanup';
import { getSentryEnvironment } from 'utils/sentryEnvironment';

export const stripUrlFragment = url => (typeof url === 'string' ? url.split('#')[0] : url);

export const scrubSentryBreadcrumb = breadcrumb => {
  if (!breadcrumb?.data) return breadcrumb;
  for (const key of ['from', 'to', 'url']) {
    breadcrumb.data[key] = stripUrlFragment(breadcrumb.data[key]);
  }
  return breadcrumb;
};

export const scrubSentryEvent = event => {
  if (event.request?.url) event.request.url = stripUrlFragment(event.request.url);
  if (event.transaction) event.transaction = stripUrlFragment(event.transaction);
  event.breadcrumbs?.forEach(scrubSentryBreadcrumb);
  return event;
};

export const scrubSentrySpan = span => {
  span.name = stripUrlFragment(span.name);
  if (span.attributes?.['url.full']) {
    span.attributes['url.full'] = stripUrlFragment(span.attributes['url.full']);
  }
  return span;
};

export const scrubSentryReplayRecording = recording => {
  if (typeof recording.data?.href === 'string') {
    recording.data.href = stripUrlFragment(recording.data.href);
  }
  if (recording.data?.tag === 'breadcrumb') {
    scrubSentryBreadcrumb(recording.data.payload);
  }
  return recording;
};

legacyCookieCleanup();

// Sentry DSN - public key, safe to include in client-side code
const SENTRY_DSN =
  'https://741163e46fdb635c93c825be66150992@o4512151858708480.ingest.de.sentry.io/4512151881252944';

try {
  Sentry.init({
    dsn: SENTRY_DSN,

    // Release for source map matching (set at build time in CI; e.g. ommnio-web@0.1.4)
    ...(typeof process.env.SENTRY_RELEASE !== 'undefined' && process.env.SENTRY_RELEASE
      ? { release: process.env.SENTRY_RELEASE }
      : {}),

    // Set sample rates for performance monitoring
    // Reduced development rate to avoid quota limits
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.01 : 0.1,

    // Set sample rates for session replay
    // Capture 1% of all sessions, plus 100% of sessions with an error
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,

    environment: getSentryEnvironment(),

    // v11 removed sendDefaultPii. Leaving dataCollection unset keeps the previous
    // sendDefaultPii: true behavior (IP, headers, cookies, and request bodies).
    // Session Replay stays masked via replayIntegration below.

    // Set trace propagation targets
    // Only trace API requests to avoid unnecessary CORS preflight requests
    tracePropagationTargets: url => {
      // Trace API endpoints: URLs containing /api/ path
      if (url.includes('/api/')) {
        return true;
      }
      // Trace api.ommnio.com subdomain (e.g., api.ommnio.com, web-api.ommnio.com)
      if (/^https:\/\/[^/]*api[^/]*\.ommnio\.com/.test(url)) {
        return true;
      }
      // Don't trace other requests (file requests, static assets, etc.)
      return false;
    },

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Session replay for debugging
      Sentry.replayIntegration({
        // Mask all text content and media by default for privacy
        maskAllText: true,
        blockAllMedia: true,
        beforeAddRecordingEvent: scrubSentryReplayRecording,
      }),
    ],

    beforeBreadcrumb: scrubSentryBreadcrumb,

    // Filter out known non-critical errors and store event ID for feedback association
    beforeSend(event, hint) {
      scrubSentryEvent(event);

      // Store the event ID so it can be used for feedback association
      // This prevents feedback from being marked as spam by ensuring it's linked to an error
      // event.event_id is available in beforeSend - it's generated before this hook is called
      if (event.event_id && typeof window !== 'undefined') {
        window.__SENTRY_LAST_ERROR_EVENT_ID__ = event.event_id;
      }

      // Ignore 401 errors - these trigger a logout redirect and are expected
      const error = hint.originalException || hint.syntheticException;
      if (error && typeof error === 'object' && error.status === 401) {
        return null;
      }

      // Ignore errors from browser extensions
      if (event.exception) {
        if (error && typeof error === 'object' && error.message) {
          const errorMessage = error.message.toLowerCase();
          // Filter out common browser extension errors
          if (
            errorMessage.includes('chrome-extension://') ||
            errorMessage.includes('moz-extension://') ||
            errorMessage.includes('safari-extension://')
          ) {
            return null;
          }
        }
      }

      return event;
    },
    beforeSendSpan(span) {
      return scrubSentrySpan(span);
    },
  });
} catch (error) {
  console.error('Failed to initialize Sentry:', error);
}

// Expose Sentry globally in development for easier testing
if (process.env.NODE_ENV === 'development') {
  window.Sentry = Sentry;
}
